<template>
  <div class="community-users">
    <div class="tw-container px-4 py-6">

      <!-- Header -->
      <div class="mb-6 tw-flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 class="text-2xl font-bold text-gray-900">{{ $t('community_users_title') }}</h2>
          <p class="text-sm text-gray-500 mt-1">
            {{ numberOfResults !== null ? $t('community_users_members_count', { count: numberOfResults }) : $t('community_users_loading') }}
          </p>
        </div>
        <div class="tw-flex items-center gap-2 flex-wrap">
          <DsButton
            v-if="isOwner || isMember"
            variant="rounded-minimal"
            icon="chart-pie"
            :label="$t('community_analytics')"
            @click="navigateToDynamicDashboard"
          />
          <DsButton
            variant="rounded"
            icon="plus"
            :label="$t('community_add_member')"
            @click="displayAddMemberModal"
          />
        </div>
      </div>

      <!-- Filters -->
      <div class="mb-6 bg-white rounded-lg shadow-sm border p-4 tw-flex flex-col sm:flex-row gap-3">
        <div class="flex-1">
          <DsInput
            v-model="filters.q"
            icon="search"
            is-simplified
            :placeholder="$t('community_users_search_placeholder')"
          />
        </div>
        <div class="sm:w-48">
          <select
            v-model="filters.role"
            class="w-full h-9 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-community focus:border-transparent"
          >
            <option :value="null">{{ $t('community_users_role_all') }}</option>
            <option value="owner">{{ $t('community_users_role_owner') }}</option>
            <option value="member">{{ $t('community_users_role_member') }}</option>
            <option value="portfolio_member">{{ $t('community_users_role_portfolio_member') }}</option>
            <option value="actor">{{ $t('community_users_role_actor') }}</option>
          </select>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="tw-flex items-center justify-center py-16">
        <Loading/>
      </div>

      <!-- Empty state -->
      <div v-else-if="users.length === 0" class="text-center py-16 px-8 bg-white rounded-lg border border-gray-200">
        <div class="bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4 text-gray-400" style="width:56px;height:56px;flex-shrink:0">
          <Icon name="users" size="24"/>
        </div>
        <p class="text-base font-semibold text-gray-700 mb-1">{{ $t('community_users_empty_title') }}</p>
        <p class="text-sm text-gray-400">{{ $t('community_users_empty_sub') }}</p>
      </div>

      <!-- User list -->
      <div v-else class="tw-flex flex-col gap-3">
        <div
          v-for="user in users"
          :key="user.id || user.email"
          class="tw-flex flex-wrap items-center gap-4 bg-white rounded-lg border border-gray-200 px-5 py-4 shadow-sm transition-shadow"
          :class="{ 'cursor-pointer hover:shadow-md': !user.invited_user }"
          @click="!user.invited_user && openUserPanel(user)"
        >
          <!-- Avatar -->
          <div
            class="w-[42px] h-[42px] rounded-full text-white font-semibold text-sm tw-flex items-center justify-center shrink-0"
          >
            <span v-if="user.invited_user"><Icon name="email-outline" size="16"/></span>
            <AvatarNew v-else :user="user"/>
          </div>

          <!-- Info -->
          <div class="tw-flex flex-col gap-0.5 flex-1 min-w-0">
            <div class="tw-flex items-center gap-2 flex-wrap">
              <span class="font-semibold text-gray-900">
                {{ user.invited_user ? $t('community_users_invited_user') : user.name }}
              </span>
              <span v-if="user.team_verified === false" class="role-badge role-badge--warning">
                {{ $t('community_users_needs_curation') }}
              </span>
              <span v-if="!user.verified && !user.invited_user" class="role-badge role-badge--warning">
                {{ $t('community_users_unverified_email') }}
              </span>
            </div>
            <a :href="'mailto:' + user.email" class="text-sm text-gray-500 hover:text-primary no-underline truncate" @click.stop>
              {{ user.email }}
            </a>
            <div v-if="!user.invited_user && user.last_activity" class="text-xs text-gray-400 mt-0.5">
              {{ $t('community_users_last_login', { date: user.last_activity }) }}
            </div>
          </div>

          <!-- Actions -->
          <div class="w-full sm:w-auto tw-flex items-center gap-2 flex-wrap justify-end" @click.stop>
            <!-- Invited user actions -->
            <template v-if="user.invited_user && canEditInvitation">
              <DsButton size="extra-small" variant="rounded-secondary" icon="email-outline" :label="$t('community_users_resend_invite')"
                        @click="showEditModal(user, 'resend-invitation')"/>
              <DsButton size="extra-small" variant="rounded-secondary" icon="remove" :label="$t('community_users_cancel_invite')"
                        @click="showEditModal(user, 'cancel-invitation')"/>
            </template>

            <!-- Registered user actions -->
            <template v-else-if="!user.invited_user">
              <DsButton
                v-if="user.verified === false"
                size="extra-small"
                variant="rounded-secondary"
                icon="email-outline"
                :label="$t('community_users_resend_verify')"
                @click="showEditModal(user, 'resend-confirmation')"
              />
              <template v-if="user.team_verified === false && canEditUser(user)">
                <DsButton size="extra-small" variant="rounded" icon="check" :label="$t('community_users_accept')" @click="showEditModal(user, 'accept')"/>
                <DsButton size="extra-small" variant="rounded-secondary" :label="$t('community_users_deny')" @click="showEditModal(user, 'deny')"/>
              </template>
              <DsButton
                v-if="user.id !== userId && canShowConversations"
                size="extra-small"
                variant="rounded-secondary"
                :label="hasConversation(user) ? $t('community_users_open_chat') : $t('community_users_chat')"
                @click="openOrStartConversation(user)"
              />
              <DsButton
                v-if="user.id !== userId"
                size="small"
                variant="rounded-secondary"
                icon="trash"
                :label="$t('community_users_remove')"
                @click="confirmRemoveFromCommunity(user)"
              />
            </template>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <Pagination
        v-if="paging && paging.total > paging.limit"
        class="simple-pagination--bottom mt-4"
        :paging="paging"
        @change="setOffset"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import _throttle from 'lodash/throttle'
  import DsInput from '@/components/Form/DsInput.vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import Loading from '@/components/Dashboard/ConceptMap/Loading.vue'
  import Pagination from '@/components/Pagination/SimplePagination.vue'

  import { fetchUserDetail, fetchUsers } from '@/api/config'
  import { removeMember } from '@/api/communities'
  import { Conversations } from '@/api/conversations.js'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '@/store/modules/conversations.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui.js'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import TranslationsMixin from '@/util/TranslationsMixin.js'
  import UserMixin from '@/util/UserMixin'
  import CommunityMixin from '@/util/CommunityMixin'
  import { ConversationsMixin } from '@/util/ConversationsMixin.js'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'

  export default defineComponent({
    name: 'CommunityUsers',
    components: { AvatarNew, DsInput, DsButton, Icon, Loading, Pagination },
    mixins: [TranslationsMixin, UserMixin, CommunityMixin, ConversationsMixin],
    props: {
      communityId: {
        type: [Number, String],
        required: true,
      },
    },
    data() {
      return {
        users: [] as any[],
        loading: false,
        paging: {} as any,
        numberOfResults: null as number | null,
        filters: {
          limit: 50,
          offset: null as number | null,
          q: null as string | null,
          verified: null as boolean | null,
          role: null as string | null,
          community: this.communityId,
        },
      }
    },
    computed: {
      canAddEcosystemMembers(): boolean {
        return this.$store.getters.isPublisherEnabled && (this.isMember || this.isOwner)
      },
      canEditInvitation(): boolean {
        return this.isOwner
      },
      conversations(): any[] {
        return this.$store.state.conversations.listData.data
      },
      canShowConversations(): boolean {
        return this.hasAccessToConversations && this.conversations.length > 0
      },
    },
    methods: {
      initials(name: string): string {
        if (!name) return '?'
        return name.split(' ').slice(0, 2).map((n: string) => n[0]).join('').toUpperCase()
      },
      normaliseRole(role: string): string {
        const key = `community_users_role_${role}`
        return this.$t(key) || role || ''
      },
      roleBadgeClass(role: string): string {
        const map: Record<string, string> = {
          owner: 'role-badge--owner',
          member: 'role-badge--member',
          portfolio_member: 'role-badge--portfolio',
          actor: 'role-badge--actor',
        }
        return map[role] || 'role-badge--default'
      },
      canEditUser(user: any): boolean {
        if (this.isOwner) {
          return (this.isDeveloper || this.isMember) && user.id !== this.userId
        }
        if (this.isMember) {
          return (this.isDeveloper || this.isMember) && user.id !== this.userId
            && user.role !== 'member' && user.role !== 'owner'
        }
        return false
      },
      hasConversation(user: any): boolean {
        return !!user.conversation_id
      },
      openOrStartConversation(user: any) {
        if (user.conversation_id) {
          this.$router.push('/profile/conversations/' + user.conversation_id)
          return
        }
        Conversations.post({ recipient_id: user.id }).then((conversation: any) => {
          this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
          this.$router.push('/profile/conversations/' + conversation.id)
        })
      },
      openUserPanel(user: any) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'user-information-side-panel',
          metaData: { userId: user.id, allowDelete: this.isOwner || this.isMember },
        })
      },
      showEditModal(user: any, action: string) {
        let promise: Promise<any> = Promise.resolve(user)
        if (action === 'remove') {
          promise = fetchUserDetail(user.id, 'user-management')
        }
        promise.then(u => {
          this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { user: u, action })
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL,
            action === 'remove' ? MODAL_IDS.REMOVE_USER_FROM_ECOSYSTEM : MODAL_IDS.USER_EDIT
          )
        }).catch(() => {
          this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { user, action })
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL,
            action === 'remove' ? MODAL_IDS.REMOVE_USER_FROM_ECOSYSTEM : MODAL_IDS.USER_EDIT
          )
        })
      },
      confirmRemoveFromCommunity(user: any) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          memberName: user.name,
          communityName: this.getCommunity()?.name,
          resource: user,
          modalContextType: 'community-member',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      removeCommunityMember(user: any) {
        removeMember(this.communityId, user.id).then(() => {
          this.users = this.users.filter((u: any) => u.id !== user.id)
          if (this.numberOfResults !== null) this.numberOfResults--
        })
      },
      displayAddMemberModal() {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          community: {
            name: this.community?.name,
            id: this.communityId,
          },
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_MEMBER_TO_COMMUNITY_MODAL)
      },
      navigateToDynamicDashboard() {
        this.$router.push(`/settings/ecosystem-heartbeat?community=${this.communityId}`)
      },
      exportCsv() {
        const params = new URLSearchParams({ community: String(this.communityId), export: 'true' })
        if (this.filters.q) params.set('q', this.filters.q)
        if (this.filters.role) params.set('role', this.filters.role)
        document.location.href = `/api/settings/users?${params.toString()}`
      },
      setOffset(offset: number) {
        this.filters = { ...this.filters, offset }
      },
      fetch() {
        this.loading = true
        const filters = { ...this.filters, community: this.communityId }
        fetchUsers(filters)
          .then((response: any) => {
            this.users = response.data
            this.paging = response.paging
            this.numberOfResults = response.total
          })
          .finally(() => {
            this.loading = false
          })

        this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
      },
    },
    mounted() {
      this.fetch()
      this.$bus.on('userUpdated', () => this.fetch())
      this.$bus.on('userDeleted', () => this.fetch())
      this.$bus.on('invitationsAdded', () => this.fetch())
      this.$bus.on('invited-community-member', () => this.fetch())
      this.$bus.on('communityMemberDeleteConfirmation', (ctx: any) => this.removeCommunityMember(ctx.resource))
    },
    beforeUnmount() {
      this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
      this.$bus.off('userUpdated')
      this.$bus.off('userDeleted')
      this.$bus.off('invitationsAdded')
      this.$bus.off('invited-community-member')
      this.$bus.off('communityMemberDeleteConfirmation')
    },
    watch: {
      filters: {
        deep: true,
        handler: _throttle(function (this: any) {
          this.fetch()
        }, 300, { trailing: true }),
      },
    },
  })
</script>

<style lang="scss" scoped>
  .focus\:ring-community:focus {
    --tw-ring-color: var(--primary-community, #3b82f6);
  }

  .role-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.7rem;
    font-weight: 600;
    white-space: nowrap;

    &--default {
      background: #f3f4f6;
      color: #374151;
    }

    &--warning {
      background: #fef3c7;
      color: #b45309;
    }
  }
</style>
